<script>
  import { getContext } from "svelte"
  import { getNotificationsContext } from "svelte-notifications"
  import { useQueryClient } from "@sveltestack/svelte-query"
  import DialogContent from "../../dialog/DialogContent.svelte";
  import Stack from "../../layout/Stack.svelte";
  import {
    useDispatchingViewResourcesQuery
  } from "../../../hooks/project/views/dispatchingView/useDispatchingViewResourcesQuery.js";
  import GeneralSelect from "../../select/GeneralSelect.svelte";
  import { translate } from "../../../i18n/i18next.js";
  import DialogBottomControls from "../../dialog/DialogBottomControls.svelte";
  import DialogBottomControlButton from "../../dialog/DialogBottomControlButton.svelte";
  import SearchInput from "../../input/SearchInput.svelte";
  import CheckboxButton from "../../input/CheckboxButton.svelte";
  import ManageRestrictionsTitle from "./ManageRestrictionsTitle.svelte";
  import { submitRestrictions } from "./utils.js";

  export let projectId
  export let user
  export let closeDialog

  const {addNotification} = getNotificationsContext()
  const {handleFetchErrors} = getContext("fetchErrorHandler")
  const queryClient = useQueryClient()

  let selectedResourceGroup = ""
  let searchQuery = ""
  let selectedSortOption = "name"
  let sortDescending = true;
  const sortOptions = {
    displayOrder: translate("asprova.terms.displayOrder"),
    code: translate("asprova.terms.resourceCode"),
    name: translate("asprova.terms.resourceName"),
  };
  $: getFilteredAndSortedResources = () => {
    return resources
      .filter(
        (r) =>
          selectedResourceGroup === "" || (r.group && r.group.filter(group => group.code == selectedResourceGroup).length)
      )
      .filter(
        (r) =>
          r.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          r.code.toLowerCase().includes(searchQuery.toLowerCase())
      )
      .sort((a, b) => {
        const ret = sortDescending ? 1 : -1;

        if (!a[selectedSortOption] && !b[selectedSortOption]) return 0;
        if (!a[selectedSortOption]) return ret;
        if (!b[selectedSortOption]) return -ret;

        if (b[selectedSortOption] > a[selectedSortOption]) return ret;
        if (b[selectedSortOption] < a[selectedSortOption]) return -ret;
        return 0;
      });
  };

  const resourcesQuery = useDispatchingViewResourcesQuery(projectId, handleFetchErrors, false)
  $: ({
    resources,
    resourceGroups,
  } = $resourcesQuery.data ?? {resources: [], resourceGroups: [], isCustomizedForProfile1: false})

  const resourceObjectIdToRoleMap = {}

  let isSubmitting = false

  async function submitRestrictionsForExistingUser(resourceObjectIdToRoleMap) {
    if (!user) return
    const userId = user.id

    isSubmitting = true
    const ok = await submitRestrictions(projectId, userId, resourceObjectIdToRoleMap)
    if (ok) {
      addNotification({
        text: translate("frontend:components.restrictedUserInvitationDialog.messages.restrictedUserAdded"),
        type: "success",
        removeAfter: 4000,
        position: "top-right",
      })
      await queryClient.invalidateQueries(`/api/projects/${projectId}/users`)
      closeDialog()
    } else {
      addNotification({
        text: translate("frontend:components.restrictedUserInvitationDialog.messages.restrictedUserAdditionFailed"),
        type: "error",
        removeAfter: 4000,
        position: "top-right",
      })
      isSubmitting = false
    }
  }
</script>

<DialogContent>
  <Stack direction="vertical" gap=".8rem">
    <ManageRestrictionsTitle
      user={user}
    />
    <Stack direction="vertical" gap=".5rem">
      <div style="display: flex; justify-content: end">
        <div class="top-panel-filter">
          <span>
            {translate("asprova.terms.resourceGroup")}:
          </span>
          <GeneralSelect
            extraStyle="margin-left: 10px; border: solid 2px gray;"
            options={resourceGroups.map((group) => ({
              text: group.code,
              value: group.code,
            }))}
            bind:value={selectedResourceGroup}
            defaultValue={""}
            defaultText={translate("generic.unspecified")}
          />
        </div>
      </div>
      <hr />
      <div class="control-row">
        <SearchInput
          bind:value={searchQuery}
          placeholder={translate("frontend:views.dispatchingView.searchByResourceCodeOrResourceName")}
          width="90%"
        />
        <div style="flex-grow: 1;"></div>
        <GeneralSelect
          extraStyle="margin-right: 10px; border: none; color: #00468f; font-weight:bold;"
          options={Object.entries(sortOptions).map(([key, text]) => ({
            text: text,
            value: key,
          }))}
          bind:value={selectedSortOption}
        />
        <!-- svelte-ignore a11y-click-events-have-key-events -->
        <img
          src={sortDescending ? "/assets/icon_order_descending.svg": "/assets/icon_order_ascending.svg"}
          alt=""
          on:click={() => sortDescending = !sortDescending}
          style="cursor: pointer; margin-right: 2px;"
        />
      </div>
      <div class="table-container">
        <table>
          <thead>
            <tr>
              <th>{translate("asprova.terms.resourceCode")}</th>
              <th>{translate("asprova.terms.resourceName")}</th>
              <th>
                {translate("frontend:components.restrictedUserInvitationDialog.steps.manageRestrictions.allowView")}
              </th>
              <th>
                {translate("frontend:components.restrictedUserInvitationDialog.steps.manageRestrictions.allowResultEdit")}
              </th>
            </tr>
          </thead>
          <tbody>
            {#each getFilteredAndSortedResources() as resource}
              <tr class="table-row">
                <td>{resource.code || "-"}</td>
                <td>{resource.name || "-"}</td>
                <td>
                  <CheckboxButton
                    checked={resourceObjectIdToRoleMap[resource.objectId] === "ResultInput" || resourceObjectIdToRoleMap[resource.objectId] === "ReadOnly"}
                    onClick={() => {
                      const checked = resourceObjectIdToRoleMap[resource.objectId] === "ResultInput" || resourceObjectIdToRoleMap[resource.objectId] === "ReadOnly"
                      if (!checked) resourceObjectIdToRoleMap[resource.objectId] =  "ReadOnly"
                      else resourceObjectIdToRoleMap[resource.objectId] =  ""
                    }}
                  />
                </td>
                <td>
                  <CheckboxButton
                    checked={resourceObjectIdToRoleMap[resource.objectId] === "ResultInput"}
                    onClick={() => {
                      const checked = resourceObjectIdToRoleMap[resource.objectId] === "ResultInput"
                      if (!checked) resourceObjectIdToRoleMap[resource.objectId] = "ResultInput"
                      else resourceObjectIdToRoleMap[resource.objectId] = "ReadOnly"
                    }}
                  />
                </td>
              </tr>
            {/each}
            {#if getFilteredAndSortedResources().length === 0}
              <tr>
                <td colspan="4" style="text-align:center">
                  {translate("frontend:components.filterInput.noChoiceMatched")}
                </td>
              </tr>
            {/if}
          </tbody>
        </table>
      </div>
    </Stack>
  </Stack>
</DialogContent>
<DialogBottomControls>
  <DialogBottomControlButton
    type="primary"
    onClick={async () => {
      await submitRestrictionsForExistingUser(resourceObjectIdToRoleMap)
    }}
    disabled={isSubmitting || Object.values(resourceObjectIdToRoleMap).every(val => !val)}
  >
    {translate("generic.add")}
  </DialogBottomControlButton>
  <DialogBottomControlButton type="secondary" onClick={() => closeDialog()}>
    {translate("generic.cancel")}
  </DialogBottomControlButton>
</DialogBottomControls>

<style>
  .control-row {
    display: flex;
    align-items: center;
    width: 100%;
    padding: 5px;
    box-sizing: border-box;
    margin-top: 6px;
  }

  .table-container {
    overflow-y: auto;
  }

  table {
    width: 100%;
    border-collapse: collapse;
  }

  th,
  td {
    border: 0;
    padding: 8px;
    text-align: left;
    min-width: 10px;
  }

  th {
    background-color: #f2f2f2;
    position: sticky;
    top: 0;
    z-index: 1;
  }

  .table-row:hover {
    background-color: #e0e0e0;
    cursor: pointer;
  }

  hr {
    width: 100%;
    color: lightgrey;
  }
</style>